import { useNavigate, useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';
import { upsertMacro } from 'api/Macro';
import { getMedications } from 'api/Medication';

import { ia, iaRa } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { formatDate } from 'lib/helpers/dateUtils';

import { showAlert } from 'components/shared/Alert/Alert';
import { currentPractice } from 'components/practice/practiceState';
import { clinicalNote as clinicalNoteState } from 'components/state';

import { getCustomFormResponse } from '../../../lib/getPatientHistory';
import { cleanUpCPTCodes } from '../../LoadFromPreviousNotes/lib/helpers';
import { createNoteMacroObject } from '../UpsertMacro/lib/noteMacroHelpers';

const useUpsertMacro = ({ macroType, customMacroBody }) => {
  const practice = useRecoilValue(currentPractice);
  const clinicalNote = useRecoilValue(clinicalNoteState);
  const timezone = useTimezone();

  const { type, id: patientId, appointmentId } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { isLoading, mutateAsync: upsert } = useMutation((data) => upsertMacro(navigate, data), {
    onSuccess: (data) => {
      if (data?.macro) {
        const updateFn = (oldData) => {
          const newMacros = [...iaRa(oldData?.macros)];

          const macroIndex = newMacros.findIndex((item) => item.id === data.macro.id);

          if (macroIndex > -1) {
            newMacros[macroIndex] = data.macro;
          } else {
            newMacros.unshift(data.macro);
          }

          return { ...(oldData || {}), macros: newMacros };
        };

        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const [key, type, queryFilters] = query.queryKey;
              if (key !== 'macros') return false;

              const isUnfiltered = !queryFilters || Object.keys(queryFilters).length === 0;
              const isRelevantType = type == null || type === macroType;

              return isUnfiltered && isRelevantType;
            }
          },
          updateFn
        );
      }
      showAlert({ title: data?.message, color: 'success' });
    }
  });

  const getCN = async ({ noteType } = {}) => {
    const patientIdResolved = clinicalNote?.user_id ?? patientId;
    const appointmentIdResolved = clinicalNote?.appointment_id ?? appointmentId;

    const shouldLoadMedications = !noteType || ['soap', 'hp'].includes(noteType);
    const shouldLoadForms = !noteType || noteType === 'hp';

    const [cnData, medicationsResponse, customFormResponses] = await Promise.all([
      requestApi({
        url: '/api/clinical_note/read',
        params: {
          patientId: patientIdResolved,
          appointmentId: appointmentIdResolved,
          exporting: true
        },
        navigate
      }),

      shouldLoadMedications
        ? getMedications(navigate, {
            appointmentId: appointmentIdResolved,
            patientId: patientIdResolved
          })
        : Promise.resolve([]),

      shouldLoadForms
        ? getCustomFormResponse({
            queryClient,
            patientId: patientIdResolved,
            practiceId: practice?.id,
            type: [
              'allergies',
              'vaccines',
              'familyHistory',
              'surgicalHistory',
              'weightLossAdministration'
            ],
            appointmentId: appointmentIdResolved
          })
        : Promise.resolve({})
    ]);

    const medications = medicationsResponse?.medicationHistory?.response?.medications;

    const overviewData = {
      ...customFormResponses,
      ...(ia(medications) && { medicationHistory: medications })
    };

    const clinicalNoteData = { ...cnData?.clinicalNote };

    if (ia(clinicalNoteData?.icd_10_codes)) {
      clinicalNoteData.icd_10_codes = clinicalNoteData.icd_10_codes.filter(
        (note) => note?.appointment_id == appointmentIdResolved
      );
    }

    if (ia(clinicalNoteData?.cpt_codes)) {
      const dos = formatDate(cnData?.appointment?.starts_at, timezone, 'MM/dd/yyyy');

      clinicalNoteData.cpt_codes = cleanUpCPTCodes({
        cptCodes: clinicalNoteData.cpt_codes,
        timezone,
        dos
      });
    }

    return {
      ...cnData,
      clinicalNote: clinicalNoteData,
      overviewData
    };
  };

  const createMacroType = async ({ name, body }) => {
    let response = null;

    switch (macroType) {
      case 'appointment_macro':
        response = await getCN();
        return {
          name,
          type: macroType,
          body: {
            clinicalNote: response?.clinicalNote,
            advancedHP: response?.advancedHP,
            advancedSOAP: response?.advancedSOAP,
            advancedCustomNote: response?.customFormTypes,
            overviewData: response?.overviewData,
            forms: body?.forms
          }
        };
      case 'note_macro':
        response = await getCN({ noteType: type });
        return createNoteMacroObject({
          name,
          type: macroType,
          // noteType: Object.keys(body?.forms)?.[0],
          noteType: type,
          advancedHP: response?.advancedHP,
          advancedSOAP: response?.advancedSOAP,
          advancedCustomNote: response?.customFormTypes,
          clinicalNote: response?.clinicalNote,
          overviewData: response?.overviewData,
          forms: body?.forms
        });
      case 'form_macro':
        return {
          name,
          type: macroType,
          body: {
            ...(customMacroBody?.advancedHP ||
            customMacroBody?.advancedSOAP ||
            customMacroBody?.advancedCustomNote ||
            customMacroBody?.clinicalNote
              ? customMacroBody
              : { clinicalNote: customMacroBody }),
            forms: body?.forms
          }
        };
      case 'text_macro':
        return {
          name,
          type: macroType,
          body
        };
    }
  };

  const upsertHandler = async (values) => {
    const newMacro = await createMacroType(values);

    if (values?.forms) delete values.forms;
    if (values?.note) delete values.note;

    await upsert({ ...values, ...newMacro });
  };

  return {
    upsert: upsertHandler,
    loading: isLoading
  };
};

export default useUpsertMacro;
